import React, { useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import {
  Login,
  Alumni,
  Analytics,
  FAQ,
  PrivacyPolicy,
  Help,
  DPA,
  TermsOfService,
  Applications,
  Dashboard,
  DataTracker,
  NotFound,
  Residents,
} from "./pages";
import PublicFormPage from "./pages/PublicFormPage";
import StripeOnboarding from "./StripeOnboarding.jsx";
import RCRiskModel from "./pages/RCRiskModel";
import MigrationTool from "./pages/MigrationTool.jsx";
import MedicationLog from "./pages/MedicationLog.jsx";
import { Nav, IntakeForm, Invoices, ResidentProfile } from "./components";
import Payments from "./pages/Payments.jsx";
import Schedule from "./pages/Schedule.jsx";
import ResidentDashboard from "./pages/ResidentDashboard.jsx";
import ResidentNav from "./components/ResidentNav.jsx";
import AlumniProfile from "./components/AlumniProfile.jsx";
import Owner from "./pages/Owner.jsx";
import RCPublicDashboard from "./pages/publicRCData/RCPublicDashboard.jsx";
import Gameroom from "./pages/games/Gameroom.jsx";
import MemoryMatrix from "./pages/games/MemoryMatrix.jsx";
import CategoryFluency from "./pages/games/CategoryFluency.jsx";
import SpeedReactor from "./pages/games/SpeedReactor.jsx";
import ColorClashChallenge from "./pages/games/ColorClashChallenge.jsx";
import FocusFinder from "./pages/games/FocusFinder.jsx";
import MathRace from "./pages/games/MathRace.jsx";
import Minesweeper from "./pages/games/Minesweeper.jsx";
import SudokuChallenge from "./pages/games/SudokuChallenge.jsx";
import RuleShifter from "./pages/games/RuleShifter.jsx";
import BreathPacer from "./pages/games/BreathPacer.jsx";
import DataStream from "./pages/games/DataStream.jsx";
import SnakeChallenge from "./pages/games/SnakeChallenge.jsx";
import StopSignalMulti from "./pages/games/StopSignalMulti.jsx";
import VisualSearchMulti from "./pages/games/VisualSearchMulti.jsx";
import SpanTaskMulti from "./pages/games/SpanTaskMulti.jsx";
import OldSchoolTetris from "./pages/games/OldSchoolTetris.jsx";
import VisualSearchTask from "./pages/games/VisualSearchTask.jsx";
import RuleShifterMulti from "./pages/games/RuleShifterMulti.jsx";
import InspectionTimeMulti from "./pages/games/InspectionTimeMulti.jsx";
import MemoryMatrixMulti from "./pages/games/MemoryMatrixMulti.jsx";
import ColorClashMulti from "./pages/games/ColorClashMulti.jsx";
import FocusFinderMulti from "./pages/games/FocusFinderMulti.jsx";
import MathRaceMulti from "./pages/games/MathRaceMulti.jsx";
import DataStreamMulti from "./pages/games/DataStreamMulti.jsx";
import StopSignalChallenge from "./pages/games/StopSignalChallenge.jsx";
import SpanTask from "./pages/games/SpanTask.jsx";
import InspectionTime from "./pages/games/InspectionTime.jsx";
import RecoveryCapital from "./components/RecoveryCapital.jsx";
import { Spinner } from "./components";
import { BedManager, HouseView } from "./components/BedManager";
import RecoveryCapitalData from "./pages/RecoveryCapitalData.jsx";
import LandingPage from "./pages/LandingPage.jsx";
import PendingApproval from "./pages/PendingApproval.jsx";
import Phases from "./pages/Phases.jsx";
import FirstTimeSetup from "./pages/auth/FirstTimeSetup.jsx";
import Waitlist from "./pages/Waitlist.jsx";
import Ua from "./pages/Ua.jsx";
import { useAuth, ProtectedRoute } from "./hooks/AuthenticationProvider";
import PlatformManager from "./pages/PlatformManager";
import Sponsorship from "./pages/Sponsorship";
import Forms from "./pages/Forms";
import FeatureRoute from "./hooks/FeatureRoute";
import { SubscriptionProvider } from "./utils/SubscriptionManager";

const gameRoutes = [
  { path: "/gameroom", component: Gameroom },
  { path: "/memorymatrix", component: MemoryMatrix },
  { path: "/visualsearchtask", component: VisualSearchTask },
  { path: "/spantask", component: SpanTask },
  { path: "/stopsignal", component: StopSignalChallenge },
  { path: "/speedreactor", component: SpeedReactor },
  { path: "/ruleshifter", component: RuleShifter },
  { path: "/focusfinder", component: FocusFinder },
  { path: "/breathpacer", component: BreathPacer },
  { path: "/datastream", component: DataStream },
  { path: "/inspectiontime", component: InspectionTime },
  { path: "/mathrace", component: MathRace },
  { path: "/colorclash", component: ColorClashChallenge },
  { path: "/categoryfluency", component: CategoryFluency },
  { path: "/sudoku", component: SudokuChallenge },
  { path: "/colormulti", component: ColorClashMulti },
  { path: "/datastreammulti", component: DataStreamMulti },
  { path: "/focusfindermulti", component: FocusFinderMulti },
  { path: "/mathracemulti", component: MathRaceMulti },
  { path: "/memorymatrixmulti", component: MemoryMatrixMulti },
  { path: "/ruleshiftermulti", component: RuleShifterMulti },
  { path: "/snake", component: SnakeChallenge },
  { path: "/tetris", component: OldSchoolTetris },
  { path: "/stopsignalmulti", component: StopSignalMulti },
  { path: "/inspectiontimemulti", component: InspectionTimeMulti },
  { path: "/spantaskmulti", component: SpanTaskMulti },
  { path: "/visualsearchmulti", component: VisualSearchMulti },
  { path: "/minesweeper", component: Minesweeper },
];

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    pendingStatus,
    organizationLoaded,
    isLoading,
    firebaseLoaded,
    organizationId,
    role,
  } = useAuth();

  // Add this state for loading timeout
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Add this effect to handle loading timeout
  useEffect(() => {
    let timeoutId = null;

    if ((!firebaseLoaded || isLoading) && !loadingTimeout) {
      timeoutId = setTimeout(() => {
        setLoadingTimeout(true);
        navigate("/login", { replace: true });
      }, 8000); // 8 seconds timeout
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [firebaseLoaded, isLoading, loadingTimeout, navigate]);

  // Which emails can access the platform manager
  const isPlatformManagerAllowed =
    user?.email === "adam@reverencehouse.com" ||
    user?.email === "recoveryhouseplatform@gmail.com";

  const urlParams = new URLSearchParams(location.search);

  // Routes that must NOT show the standard Nav
  const navExcludedRoutes = [
    "/welcome",
    "/login",
    "/signup",
    "/setup",
    "/analytics-public",
    "/gameroom",
    "/pending-approval",
    "/faq",
    "/privacy-policy",
    "/terms-of-service",
    "/dpa",
  ];

  const isResidentDashboard = location.pathname === "/residentdashboard";
  const isPublicFormRoute = location.pathname.startsWith("/public-forms");
  const isPublicAnalyticsRoute = location.pathname === "/analytics-public";
  const shouldRenderNav =
    !navExcludedRoutes.includes(location.pathname) && !isPublicFormRoute;

  // Updated loading check to include timeout
  if ((!firebaseLoaded || isLoading) && !loadingTimeout) {
    return <Spinner />;
  }

  // Allow residents access to dashboard + game routes
  const residentAllowedPaths = gameRoutes
    .map((r) => r.path)
    .concat("/residentdashboard");

  if (
    isAuthenticated &&
    organizationLoaded &&
    role === "Resident" &&
    !residentAllowedPaths.includes(location.pathname)
  ) {
    return <Navigate to="/residentdashboard" replace />;
  }

  // Add this new block right after the resident redirect
  if (
    isAuthenticated &&
    organizationLoaded &&
    role === "Leader" &&
    location.pathname === "/residentdashboard" &&
    !sessionStorage.getItem("viewingResidentId")
  ) {
    // If Leader tries to access resident dashboard without proper session, redirect back
    return <Navigate to="/" replace />;
  }

  if (location.pathname === "/login" && pendingStatus === "Completed") {
    return <Navigate to="/" replace />;
  }

  if (
    pendingStatus === "Pending" &&
    location.pathname !== "/pending-approval"
  ) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (pendingStatus === "Approved" && location.pathname !== "/setup") {
    return <Navigate to="/setup" replace />;
  }

  if (location.pathname === "/" && !isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <>
      {shouldRenderNav && !isResidentDashboard && (
        <SubscriptionProvider organizationId={organizationId}>
          <Nav />
        </SubscriptionProvider>
      )}
      {isResidentDashboard && <ResidentNav />}

      <main
        className={`
          ${shouldRenderNav && !isResidentDashboard ? "xl:ml-64" : ""}
          ${shouldRenderNav ? "mt-14 p-6" : ""}
        `}
      >
        <Routes>
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/setup" element={<FirstTimeSetup />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/dpa" element={<DPA />} />
          <Route path="/help" element={<Help />} />
          <Route path="/analytics-public" element={<RCPublicDashboard />} />

          {/* Game routes */}
          {gameRoutes.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute
                  allowedRoles={[
                    "Resident",
                    "Owner",
                    "Admin",
                    "Staff",
                    "Leader",
                  ]}
                >
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}

          {/* Public Form route => no nav => isPublicFormRoute */}
          <Route
            path="/public-forms/:orgId/:formId"
            element={<PublicFormPage />}
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute
                requiredPermission="viewDashboard"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {isPlatformManagerAllowed && (
            <Route
              path="/platform-manager"
              element={
                <ProtectedRoute
                  allowedEmails={[
                    "adam@reverencehouse.com",
                    "recoveryhouseplatform@gmail.com",
                  ]}
                >
                  <PlatformManager />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/admin/migration"
            element={
              <ProtectedRoute allowedEmails={["adam@reverencehouse.com"]}>
                <MigrationTool />
              </ProtectedRoute>
            }
          />
          <Route
            path="/residentdashboard"
            element={
              <ProtectedRoute
                allowedRoles={["Owner", "Admin", "Staff", "Leader", "Resident"]}
              >
                <ResidentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/residents"
            element={
              <ProtectedRoute
                requiredPermission="viewResidents"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Residents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms"
            element={
              <FeatureRoute feature="customForms">
                <ProtectedRoute
                  requiredPermission="createForms"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Forms />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <FeatureRoute feature="applications">
                <ProtectedRoute
                  requiredPermission="seeApplications"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Applications />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/profile/:residentId"
            element={
              <ProtectedRoute
                requiredPermission="viewProfile"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <ResidentProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake"
            element={
              <ProtectedRoute
                requiredPermission="canAddResidents"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <IntakeForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <FeatureRoute feature="payments">
                <ProtectedRoute
                  requiredPermission="viewPayments"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Payments />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/medlog/:residentId"
            element={
              <ProtectedRoute
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <MedicationLog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/phases"
            element={
              <FeatureRoute feature="phases">
                <ProtectedRoute
                  requiredPermission="viewPhases"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Phases />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/payments/invoices"
            element={
              <FeatureRoute feature="payments">
                <ProtectedRoute
                  requiredPermission="viewInvoice"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Invoices />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/beds"
            element={
              <FeatureRoute feature="bedPlatform">
                <ProtectedRoute
                  requiredPermission="viewBeds"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <BedManager />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/beds/:houseName"
            element={
              <FeatureRoute feature="bedPlatform">
                <ProtectedRoute
                  requiredPermission="viewResidents"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <HouseView />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/alumni"
            element={
              <ProtectedRoute
                requiredPermission="viewAlumni"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Alumni />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alumni/:residentId"
            element={
              <ProtectedRoute
                requiredPermission="viewAlumni"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <AlumniProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <FeatureRoute feature="analytics">
                <ProtectedRoute
                  requiredPermission="viewAnalytics"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Analytics />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/recoverycapital"
            element={
              <FeatureRoute feature="recoveryCapital">
                <ProtectedRoute
                  requiredPermission="addRecoveryCapital"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <RecoveryCapital />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />

          <Route
            path="/recoverycapitaldata"
            element={
              <FeatureRoute feature="recoveryCapital">
                <ProtectedRoute
                  requiredPermission="viewRecoveryCapital"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <RecoveryCapitalData />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/riskmodel"
            element={
              <FeatureRoute feature="recoveryCapital">
                <ProtectedRoute
                  requiredPermission="viewRecoveryCapital"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <RCRiskModel />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute
                requiredPermission="updateSchedule"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Schedule />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <FeatureRoute feature="ownerDashboard">
                <ProtectedRoute requiredPermission="accessOwnerDashboard">
                  <Owner />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="/datatracker"
            element={
              <ProtectedRoute
                requiredPermission="canViewAnalyticData"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <DataTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/waitlist"
            element={
              <ProtectedRoute
                requiredPermission="viewWaitlist"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Waitlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ua"
            element={
              <ProtectedRoute
                requiredPermission="viewUa"
                allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
              >
                <Ua />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stripe-setup"
            element={
              <ProtectedRoute allowedRoles={["Owner", "Admin"]}>
                <StripeOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sponsorship"
            element={
              <FeatureRoute feature="sponsorship">
                <ProtectedRoute
                  requiredPermission="viewSponsorship"
                  allowedRoles={["Owner", "Admin", "Staff", "Leader"]}
                >
                  <Sponsorship />
                </ProtectedRoute>
              </FeatureRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </main>
    </>
  );
};

const App = () => {
  return <AppContent />;
};

export default App;
